import { useRef, useState } from 'react';

import { Button, Drawer, DrawerTrigger, toast } from '@ekhodealer/ekho-common/rev';

import { AddMemberForm, type AddMemberFormRef } from './AddMemberForm';

/**
 * AddMemberDrawer — Drawer for inviting new team members via comma-separated email input.
 *
 * Uses the DrawerTrigger + Drawer compound pattern with Drawer.Content
 * and Drawer.Footer slots. Uses AddMemberForm component for the form logic.
 */
export function AddMemberDrawer(): React.ReactNode {
	const formRef = useRef<AddMemberFormRef>(null);
	const [hasValidEmail, setHasValidEmail] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const handleSubmit = async (): Promise<void> => {
		await formRef.current?.submit();
		// Note: The form's onSuccess callback will handle showing the toast and closing the drawer
	};

	return (
		<DrawerTrigger>
			<Button variant='filled' size='medium'>
				Add member
			</Button>
			<Drawer placement='right'>
				<Drawer.Header>Add member</Drawer.Header>
				<Drawer.Content>
					{(closeDrawer) => {
						const handleSuccess = (emailCount: number): void => {
							toast({
								message: `${emailCount} invite${emailCount === 1 ? '' : 's'} sent successfully`,
								variant: 'success',
							});
							closeDrawer();
						};

						return (
							<>
								<AddMemberForm
									ref={formRef}
									onSuccess={handleSuccess}
									onValidationChange={(valid, loading) => {
										setHasValidEmail(valid);
										setIsLoading(loading);
									}}
								/>
							</>
						);
					}}
				</Drawer.Content>
				<Drawer.Footer>
					<Button
						size='large'
						isFullWidth
						onPress={() => {
							void handleSubmit();
						}}
						isDisabled={!hasValidEmail}
						isLoading={isLoading}>
						Send invites
					</Button>
				</Drawer.Footer>
			</Drawer>
		</DrawerTrigger>
	);
}
